import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../../../stores/store";
import SetupPromptModal from "../../../../common/ekedc-components/setup-prompt-modal/SetupPromptModal";
import { LighteningPopUpBackground } from "../../../../assets/icons";
import idCardGif from "../../../../assets/gifs/id_card.gif";
import { ApplicationRoutes } from "../../../../routes";
import { useInitiateNinPhoneVerificationMutation } from "../../../../service/vending-service";
import { getErrorMessage } from "../../../../utils/error-handler";

interface VerifyNinModalProps {
	onClose: () => void;
	// "phone": verify the NIN phone number (initiate OTP flow). "nin": the NIN itself is
	// unverified — route to the energy-profile NIN page instead.
	mode?: "phone" | "nin";
}

// Shown on the Account Activities page when the customer's NIN (or NIN phone number) is not
// verified — generating a statement requires both. Reuses the shared SetupPromptModal.
export const VerifyNinModal: React.FC<VerifyNinModalProps> = ({ onClose, mode = "phone" }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { meterNumber, accountNumber } = useSelector((state: RootState) => state.customerEnergyProfile);
	const displayNumber = meterNumber || accountNumber || "—";

	const [initiateNinPhoneVerification, { isLoading }] = useInitiateNinPhoneVerificationMutation();

	const handleVerify = async () => {
		if (mode === "nin") {
			navigate(`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.ENERGY_PROFILE_NIN}`);
			return;
		}
		if (isLoading) return;
		try {
			const phoneNumber = await initiateNinPhoneVerification().unwrap();
			navigate(`/${ApplicationRoutes.CUSTOMER_ACCOUNT_ACTIVITIES_NIN}`, { state: { phoneNumber } });
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data));
		}
	};

	return (
		<SetupPromptModal
			background={<LighteningPopUpBackground />}
			icon={<img src={idCardGif} alt="" style={{ width: "100%", height: "100%" }} />}
			badge={t("common.labels.energyProfile")}
			title={displayNumber}
			description={t("account.web.verifyNinStatementDescription")}
			ctaText={isLoading ? t("account.web.sending") : t("account.web.verifyNin")}
			onCta={handleVerify}
			onClose={onClose}
		/>
	);
};

export default VerifyNinModal;
